import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import * as Lucide from "lucide-react";
import { toast } from "sonner";

const EVENT_COLOR = {
  ok: "text-emerald-300 border-emerald-500/30 bg-emerald-500/10",
  duplicate_txn: "text-amber-300 border-amber-500/30 bg-amber-500/10",
  no_pending_match: "text-slate-400 border-white/10 bg-white/[0.02]",
  missing_amount: "text-rose-300 border-rose-500/30 bg-rose-500/10",
  race_condition: "text-rose-300 border-rose-500/30 bg-rose-500/10",
};

export default function AdminUPIWebhook() {
  const [cfg, setCfg] = useState(null);
  const [events, setEvents] = useState([]);
  const [reveal, setReveal] = useState(false);
  const [rotating, setRotating] = useState(false);

  const load = () => {
    api.get("/admin/upi/webhook/config").then((r) => setCfg(r.data));
    api.get("/admin/upi/autoconfirm/events?limit=50").then((r) => setEvents(r.data)).catch(() => setEvents([]));
  };
  useEffect(() => {
    load();
    const t = setInterval(() => api.get("/admin/upi/autoconfirm/events?limit=50").then((r) => setEvents(r.data)).catch(() => {}), 15_000);
    return () => clearInterval(t);
  }, []);

  const rotate = async () => {
    if (!window.confirm("Rotate the webhook secret? Any existing SMS forwarder app using the old secret will start failing — you'll need to update it.")) return;
    setRotating(true);
    try {
      await api.post("/admin/upi/webhook/rotate");
      toast.success("Secret rotated");
      setReveal(true);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setRotating(false); }
  };

  const copy = async (txt, label) => {
    try {
      await navigator.clipboard.writeText(txt);
      toast.success(`${label} copied`);
    } catch { toast.error("Copy failed"); }
  };

  if (!cfg) {
    return <div className="text-slate-400 text-sm flex items-center gap-2 mt-4"><Lucide.Loader2 className="animate-spin" size={14} /> Loading…</div>;
  }

  const matchedCount = events.filter((e) => e.matched).length;
  const sampleCurl = `curl -X POST ${cfg.url} \\\n  -H "Content-Type: application/json" \\\n  -H "X-Signature: <hex(hmac_sha256(secret, body))>" \\\n  -d '{"merchant_reference_id":"BX-A1B2C3D4","amount":1234.50,"upi_transaction_id":"412345678901","sender_name":"Rahul S","sender_bank":"HDFC"}'`;
  const sampleSimple = `${cfg.url}?key=${reveal ? cfg.secret : "<your-secret>"}`;

  return (
    <div className="space-y-4 mt-4" data-testid="webhook-panel">
      <div className="bx-card p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="bx-heading text-lg font-bold flex items-center gap-2">
              <Lucide.Webhook size={18} className="text-purple-300" /> Auto-confirm webhook
            </div>
            <p className="text-xs text-slate-400 mt-1 max-w-2xl">
              Point an Android SMS-forwarder app (e.g. "SMS to URL Forwarder", "AutoResponder") at this URL — every bank credit SMS instantly marks the matching log as <strong>paid</strong>.
              Matching priority: <strong className="text-purple-300">merchant_reference_id</strong> first (exact, 100% reliable), then ₹ amount within 24h pending. If your forwarder can extract our <span className="bx-mono text-purple-300">BX-XXXXXXXX</span> reference from the SMS body, include it.
            </p>
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Confirmed</div>
            <div className="text-2xl font-extrabold text-emerald-300">{matchedCount}</div>
            <div className="text-[10px] text-slate-500">of {events.length} events</div>
          </div>
        </div>

        <div className="mt-5">
          <label className="text-xs text-slate-400">Webhook URL</label>
          <div className="flex gap-2 mt-1">
            <input data-testid="webhook-url" readOnly value={cfg.url} className="bx-input bx-mono text-xs flex-1" />
            <button onClick={() => copy(cfg.url, "URL")} className="bx-btn-ghost text-xs"><Lucide.Copy size={12} /></button>
          </div>
        </div>

        <div className="mt-3">
          <label className="text-xs text-slate-400 flex items-center justify-between">
            <span>Shared secret</span>
            <button onClick={() => setReveal((v) => !v)} className="text-purple-300 hover:text-purple-200 text-[11px]" data-testid="webhook-reveal-toggle">
              {reveal ? <><Lucide.EyeOff size={11} className="inline" /> Hide</> : <><Lucide.Eye size={11} className="inline" /> Reveal</>}
            </button>
          </label>
          <div className="flex gap-2 mt-1">
            <input data-testid="webhook-secret" readOnly value={reveal ? cfg.secret : cfg.masked} className="bx-input bx-mono text-xs flex-1" />
            <button data-testid="webhook-copy-secret" onClick={() => copy(cfg.secret, "Secret")} className="bx-btn-ghost text-xs"><Lucide.Copy size={12} /></button>
            <button data-testid="webhook-rotate" onClick={rotate} disabled={rotating} className="bx-btn-ghost text-xs text-rose-300 disabled:opacity-50">
              {rotating ? <Lucide.Loader2 className="animate-spin" size={12} /> : <Lucide.RotateCcw size={12} />} Rotate
            </button>
          </div>
        </div>

        <div className="mt-5 pt-4 border-t border-white/5">
          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400 mb-2">Quick start — SMS forwarder (simple)</div>
          <div className="text-xs text-slate-400 mb-2">If your forwarder app can't sign requests, use the URL with the secret as a <span className="bx-mono">?key=</span> param:</div>
          <div className="rounded-lg bg-black/40 border border-white/5 p-3 text-[11px] bx-mono text-slate-200 break-all">{sampleSimple}</div>
          <button onClick={() => copy(`${cfg.url}?key=${cfg.secret}`, "Simple URL")} className="bx-btn-ghost text-xs mt-2"><Lucide.Copy size={12} /> Copy URL with key</button>

          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400 mt-5 mb-2">Or signed (recommended)</div>
          <div className="text-xs text-slate-400 mb-2">If your forwarder supports HMAC-SHA256, set the signature header instead of the query key:</div>
          <pre className="rounded-lg bg-black/40 border border-white/5 p-3 text-[11px] bx-mono text-slate-300 whitespace-pre-wrap overflow-x-auto">{sampleCurl}</pre>
        </div>

        <div className="mt-5 pt-4 border-t border-white/5 text-[11px] text-slate-500">
          <div className="flex items-start gap-2">
            <Lucide.Info size={12} className="mt-0.5 flex-shrink-0" />
            <div>
              <strong className="text-slate-300">Also enabled:</strong> Bank credit-alert emails received via your IMAP support inbox are parsed automatically (HDFC / SBI / ICICI / Axis / Kotak templates supported). Tickets are <em>not</em> created from these emails.
            </div>
          </div>
        </div>
      </div>

      <div className="bx-card p-5" data-testid="webhook-events">
        <div className="flex items-center justify-between">
          <div className="bx-heading text-base font-bold flex items-center gap-2">
            <Lucide.History size={16} className="text-purple-300" /> Recent auto-confirm events
          </div>
          <button onClick={load} className="bx-btn-ghost text-xs"><Lucide.RefreshCw size={11} /> Refresh</button>
        </div>
        <div className="mt-3 space-y-2">
          {events.map((e) => {
            const colors = EVENT_COLOR[e.reason] || EVENT_COLOR.no_pending_match;
            return (
              <div key={e.id} className={`flex items-start gap-3 p-3 rounded-lg border ${colors}`} data-testid={`webhook-event-${e.id.slice(0, 8)}`}>
                {e.matched ? <Lucide.CheckCircle2 size={14} className="mt-0.5 flex-shrink-0" /> : <Lucide.XCircle size={14} className="mt-0.5 flex-shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-xs flex-wrap">
                    <span className="font-semibold uppercase tracking-wider text-[10px]">{e.reason.replace(/_/g, " ")}</span>
                    {e.via && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/5 text-slate-300">via {e.via}</span>}
                    <span className="text-slate-500">·</span>
                    <span className="bx-mono text-purple-300">{e.source}</span>
                    {e.amount && <><span className="text-slate-500">·</span><span className="font-semibold">₹{Number(e.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></>}
                    {e.merchant_reference_id && <><span className="text-slate-500">·</span><span className="bx-mono text-[10px] text-purple-300/80">{e.merchant_reference_id}</span></>}
                    {e.upi_transaction_id && <><span className="text-slate-500">·</span><span className="bx-mono text-[10px] text-slate-400">{e.upi_transaction_id}</span></>}
                  </div>
                  <div className="text-[10px] text-slate-500 mt-1">{new Date(e.created_at).toLocaleString()}{e.customer_mobile ? ` · matched mobile ${e.customer_mobile}` : ""}</div>
                </div>
              </div>
            );
          })}
          {!events.length && <div className="text-center text-slate-500 text-sm py-12">No auto-confirm events yet. Set up the webhook on your phone and your first paid SMS will show here.</div>}
        </div>
      </div>
    </div>
  );
}
